/**
 * 文档相似度工具
 * 支持上传 3-4 份以上文件进行两两比较，分析相似度和雷同情况
 * 逐对调用后端接口，展示比较进度和耗时
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { fmtDateTimeCST } from '../../utils/datetime';
import {
  DocumentPlusIcon,
  XMarkIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  ScaleIcon,
  ExclamationTriangleIcon,
  StopIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { getErrorMessage, API_BASE_URL } from '../../services/api';
import axios from 'axios';

interface CompareResult {
  file1: string;
  file2: string;
  similarity_score: number;
  analysis: string;
}

interface ComparePairResponse {
  success: boolean;
  message: string;
  result: CompareResult | null;
}

interface CompareSummaryResponse {
  success: boolean;
  message: string;
  overall_summary: string;
}

interface PairInfo {
  fileA: File;
  fileB: File;
  nameA: string;
  nameB: string;
}

// ---- 持久化 ----

interface SavedComparison {
  id: number;
  file_names: string[];
  results: CompareResult[];
  overall_summary: string;
  total_pairs: number;
  elapsed_str: string;
  created_at: string;
}

async function loadComparisonHistory(): Promise<SavedComparison[]> {
  try {
    const token = localStorage.getItem('auth_token');
    const res = await axios.get(`${API_BASE_URL}/api/tools/comparison-history`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.success ? res.data.items : [];
  } catch {
    return [];
  }
}

async function saveComparisonHistory(data: {
  file_names: string[];
  results: CompareResult[];
  overall_summary: string;
  total_pairs: number;
  elapsed_str: string;
}): Promise<SavedComparison | null> {
  try {
    const token = localStorage.getItem('auth_token');
    const res = await axios.post(`${API_BASE_URL}/api/tools/comparison-history`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.success ? res.data.item : null;
  } catch {
    return null;
  }
}

/**
 * 将秒数格式化为 mm:ss 或 hh:mm:ss 的易读字符串
 */
function formatElapsed(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function BidComparison() {
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<CompareResult[]>([]);
  const [overallSummary, setOverallSummary] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);

  // 进度相关
  const [currentPair, setCurrentPair] = useState(0);     // 当前正在比较第几组（1-based）
  const [totalPairs, setTotalPairs] = useState(0);
  const [currentPairNames, setCurrentPairNames] = useState<{a: string; b: string} | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [logs, setLogs] = useState<{time: string; text: string; type: 'info' | 'success' | 'error' | 'summary'}[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortRef = useRef(false);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const elapsedRef = useRef(0);  // ref 副本，供 addLog 读取最新值

  // 历史
  const [history, setHistory] = useState<SavedComparison[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { loadComparisonHistory().then(setHistory); }, []);

  /** 添加一条日志并自动滚动到底部 */
  const addLog = useCallback((text: string, type: 'info' | 'success' | 'error' | 'summary' = 'info') => {
    const time = formatElapsed(elapsedRef.current);
    setLogs((prev) => [...prev, { time, text, type }]);
    // 延迟滚动以确保 DOM 更新
    setTimeout(() => {
      if (logContainerRef.current) {
        logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
      }
    }, 50);
  }, []);

  // 计时器：每秒 +1
  const startTimer = useCallback(() => {
    elapsedRef.current = 0;
    setElapsedSeconds(0);
    timerRef.current = setInterval(() => {
      elapsedRef.current += 1;
      setElapsedSeconds(elapsedRef.current);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => stopTimer();
  }, [stopTimer]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected) return;
    const newFiles = Array.from(selected);
    setFiles((prev) => [...prev, ...newFiles]);
    setResults([]);
    setOverallSummary('');
    setError(null);
    setFinished(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setResults([]);
    setOverallSummary('');
    setFinished(false);
  };

  /** 生成所有文件对的组合列表 */
  const buildPairs = (fileList: File[]): PairInfo[] => {
    const pairs: PairInfo[] = [];
    for (let i = 0; i < fileList.length; i++) {
      for (let j = i + 1; j < fileList.length; j++) {
        pairs.push({
          fileA: fileList[i],
          fileB: fileList[j],
          nameA: fileList[i].name,
          nameB: fileList[j].name,
        });
      }
    }
    return pairs;
  };

  const handleCompare = async () => {
    if (files.length < 2) {
      setError('请至少上传 2 份文件进行比较');
      return;
    }

    const pairs = buildPairs(files);
    setLoading(true);
    setError(null);
    setResults([]);
    setOverallSummary('');
    setFinished(false);
    setTotalPairs(pairs.length);
    setCurrentPair(0);
    setLogs([]);
    abortRef.current = false;

    startTimer();

    const token = localStorage.getItem('auth_token');
    const accumulated: CompareResult[] = [];

    // 初始日志
    addLog(`开始文档相似度比较，共 ${pairs.length} 组文件对`, 'info');

    try {
      for (let idx = 0; idx < pairs.length; idx++) {
        if (abortRef.current) break;

        const pair = pairs[idx];
        setCurrentPair(idx + 1);
        setCurrentPairNames({ a: pair.nameA, b: pair.nameB });

        addLog(`[${idx + 1}/${pairs.length}] 正在比较：${pair.nameA} vs ${pair.nameB}`, 'info');

        const formData = new FormData();
        formData.append('file_a', pair.fileA);
        formData.append('file_b', pair.fileB);

        const res = await axios.post<ComparePairResponse>(
          `${API_BASE_URL}/api/tools/compare-pair`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (res.data.success && res.data.result) {
          accumulated.push(res.data.result);
          setResults([...accumulated]);
          const score = res.data.result.similarity_score;
          addLog(
            `[${idx + 1}/${pairs.length}] 完成：相似度 ${score}% —— ${score >= 70 ? '高度雷同' : score >= 40 ? '部分雷同' : '差异较大'}`,
            score >= 70 ? 'error' : score >= 40 ? 'summary' : 'success',
          );
        } else {
          throw new Error(res.data.message || '比较失败');
        }
      }

      // 没有因取消而终止
      if (!abortRef.current && accumulated.length > 0) {
        // 调用总评接口
        setCurrentPairNames(null);
        addLog('正在生成综合评审意见...', 'summary');
        let savedSummary = '';
        try {
          const summaryRes = await axios.post<CompareSummaryResponse>(
            `${API_BASE_URL}/api/tools/compare-summary`,
            { results: accumulated },
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            },
          );
          if (summaryRes.data.success) {
            savedSummary = summaryRes.data.overall_summary;
            setOverallSummary(savedSummary);
            addLog('综合评审意见生成完毕', 'success');
          }
        } catch {
          // 总评失败不影响主流程
          savedSummary = '（总评生成失败，请查看详情）';
          setOverallSummary(savedSummary);
          addLog('综合评审意见生成失败', 'error');
        }

        addLog(`全部完成，共 ${accumulated.length} 组比较，总耗时 ${formatElapsed(elapsedSeconds)}`, 'success');
        setFinished(true);

        // 保存到历史（数据库）
        const saved = await saveComparisonHistory({
          file_names: files.map((f) => f.name),
          results: accumulated,
          overall_summary: savedSummary,
          total_pairs: pairs.length,
          elapsed_str: formatElapsed(elapsedSeconds),
        });
        if (saved) {
          setHistory((prev) => [saved, ...prev].slice(0, 20));
        }
      }
    } catch (err) {
      if (!abortRef.current) {
        const msg = getErrorMessage(err);
        setError(msg);
        addLog(`错误：${msg}`, 'error');
      }
    } finally {
      stopTimer();
      setLoading(false);
      setCurrentPairNames(null);
    }
  };

  const handleCancel = () => {
    abortRef.current = true;
    stopTimer();
    setLoading(false);
    setCurrentPairNames(null);
    addLog('用户取消比较，已完成的比较结果已保留', 'summary');
    // 保留已完成的结果供查看
    if (results.length > 0) {
      setFinished(true);
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 70) return 'text-red-600 bg-red-50 border-red-200';
    if (score >= 40) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-green-600 bg-green-50 border-green-200';
  };

  const getScoreLabel = (score: number): string => {
    if (score >= 70) return '高度雷同';
    if (score >= 40) return '部分雷同';
    return '差异较大';
  };

  // ---- 还原历史 ----

  const restoreComparison = (item: SavedComparison) => {
    setFiles([]);
    setResults(item.results);
    setOverallSummary(item.overall_summary);
    setTotalPairs(item.total_pairs);
    setFinished(true);
    setError(null);
    setLogs([]);
    setShowHistory(false);
  };

  const progressPercent = totalPairs > 0 ? Math.round((currentPair / totalPairs) * 100) : 0;

  return (
    <div className="h-full flex flex-col">
      {/* 标题 */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
          <ScaleIcon className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">文档相似度</h2>
          <p className="text-sm text-gray-500">上传多份标书文件，智能检测内容雷同与相似度</p>
        </div>
      </div>

      {/* 文件上传区域 */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-700">
            已选择 <span className="text-blue-600">{files.length}</span> 份文件
            {files.length >= 2 && (
              <span className="text-gray-400 ml-1">
                （共 {buildPairs(files).length} 组对比较）
              </span>
            )}
            {files.length < 2 && (
              <span className="text-gray-400 ml-1">（至少 2 份，推荐 3-4 份）</span>
            )}
          </span>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 cursor-pointer transition-colors">
              <DocumentPlusIcon className="w-4 h-4 mr-1.5" />
              添加文件
              <span className="text-[10px] text-gray-400 ml-1">最大 100MB</span>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.md,.markdown,.txt"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
            {history.length > 0 && (
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-gray-600 bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-colors"
              >
                <ClockIcon className="w-4 h-4 mr-1.5" />
                历史比较 ({history.length})
              </button>
            )}
          </div>
        </div>

        {/* 文件列表 */}
        {files.length > 0 ? (
          <div className="space-y-2 mb-4">
            {files.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <DocumentTextIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-700 truncate">{file.name}</span>
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    ({(file.size / 1024).toFixed(0)} KB)
                  </span>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  disabled={loading}
                  className="ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0 disabled:opacity-30"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <DocumentPlusIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">点击或拖拽上传标书文件</p>
            <p className="text-xs text-gray-400 mt-1">支持 PDF、Word、Markdown、TXT 格式</p>
          </div>
        )}

        {/* 历史比较面板 */}
        {showHistory && (
          <div className="mb-4 bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-56 overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-700">比较历史</span>
              <button onClick={() => setShowHistory(false)} className="p-0.5 text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            </div>
            {history.map((item) => (
              <button
                key={item.id}
                onClick={() => restoreComparison(item)}
                className="w-full text-left px-3 py-2 mb-1 bg-white border border-gray-200 rounded-md hover:border-blue-300 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">
                      {item.file_names.join(' · ')}
                    </p>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {fmtDateTimeCST(item.created_at)} · {item.total_pairs} 组比较 · 耗时 {item.elapsed_str}
                    </p>
                  </div>
                  {item.results.length > 0 && (
                    <span className={`text-xs font-mono font-semibold flex-shrink-0 ml-3 ${
                      item.results.every((r) => r.similarity_score < 40)
                        ? 'text-green-600'
                        : item.results.some((r) => r.similarity_score >= 70)
                          ? 'text-red-600'
                          : 'text-orange-600'
                    }`}>
                      {item.results.some((r) => r.similarity_score >= 70) ? '⚠' : ''}
                      {Math.round(item.results.reduce((s, r) => s + r.similarity_score, 0) / item.results.length)}% 均
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* 比较按钮 */}
        {!loading ? (
          <button
            onClick={handleCompare}
            disabled={files.length < 2}
            className="w-full mt-3 inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <ScaleIcon className="w-4 h-4 mr-2" />
            开始比较 ({files.length} 份文件)
          </button>
        ) : (
          /* 进度面板 */
          <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
            {/* 进度条 */}
            <div className="flex items-center gap-3 mb-3">
              <ArrowPathIcon className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-800">
                    正在比较 {currentPair}/{totalPairs} 组
                  </span>
                  <span className="text-xs text-gray-500">{progressPercent}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 rounded-full h-2 transition-all duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            </div>

            {/* 当前比较文件 & 耗时 */}
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-2 min-w-0">
                {currentPairNames && (
                  <>
                    <span className="font-medium text-gray-700 truncate max-w-[140px]">
                      {currentPairNames.a}
                    </span>
                    <span className="text-gray-400 flex-shrink-0">vs</span>
                    <span className="font-medium text-gray-700 truncate max-w-[140px]">
                      {currentPairNames.b}
                    </span>
                  </>
                )}
                {!currentPairNames && (
                  <span className="text-blue-600 font-medium">
                    正在生成综合评审意见...
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
                <ClockIcon className="w-3.5 h-3.5" />
                <span className="tabular-nums">{formatElapsed(elapsedSeconds)}</span>
              </div>
            </div>

            {/* 过程日志 */}
            {logs.length > 0 && (
              <div
                ref={logContainerRef}
                className="mt-3 bg-gray-900 rounded-lg p-3 max-h-48 overflow-y-auto font-mono text-xs leading-relaxed"
              >
                {logs.map((log, i) => (
                  <div key={i} className="flex gap-2">
                    <span className="text-gray-500 flex-shrink-0 select-none">[{log.time}]</span>
                    <span
                      className={
                        log.type === 'error'
                          ? 'text-red-400'
                          : log.type === 'success'
                            ? 'text-green-400'
                            : log.type === 'summary'
                              ? 'text-yellow-400'
                              : 'text-blue-300'
                      }
                    >
                      {log.text}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* 取消按钮 */}
            <button
              onClick={handleCancel}
              className="mt-3 w-full inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors"
            >
              <StopIcon className="w-3.5 h-3.5 mr-1.5" />
              取消比较（保留已完成结果）
            </button>
          </div>
        )}

        {/* 错误提示 */}
        {error && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* 完成提示 */}
        {finished && !loading && results.length > 0 && (
          <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-xs text-green-700">
            <ClockIcon className="w-3.5 h-3.5 flex-shrink-0" />
            已完成 {results.length}/{totalPairs} 组比较，总耗时 {formatElapsed(elapsedSeconds)}
          </div>
        )}
      </div>

      {/* 比较结果 */}
      {(results.length > 0 || overallSummary) && (
        <div className="flex-1 overflow-y-auto space-y-4">
          {/* 两两比较卡片 */}
          {results.map((r, idx) => (
            <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-gray-800 truncate max-w-[160px]">{r.file1}</span>
                  <span className="text-gray-400">vs</span>
                  <span className="font-medium text-gray-800 truncate max-w-[160px]">{r.file2}</span>
                </div>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getScoreColor(r.similarity_score)}`}
                >
                  {getScoreLabel(r.similarity_score)} · {r.similarity_score}%
                </span>
              </div>
              <div className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
                {r.analysis}
              </div>
            </div>
          ))}

          {/* 总评 */}
          {overallSummary && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">综合评审意见</h3>
              <div className="text-sm text-blue-800 whitespace-pre-wrap leading-relaxed">
                {overallSummary}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
